import React, {useCallback, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box} from '@mui/material';
import {DraftProjectStatus, ProjectType} from '@contentlab/api/project/types';
import FeatureEditor from '@contentlab/components/feature-editor';
import LanguageSelector, {
  LanguageGroup,
  LanguageOption,
} from '@contentlab/components/feature-editor/components/language-selector';
import {creditsTooltipTextForFeature} from '@contentlab/components/feature-editor/utils';
import LipSyncToggle from '@contentlab/components/lip-sync-toggle';
import {
  DEFAULT_DEBOUNCE_DELAY,
  FEATURE_BEST_PRACTICES_LOCALE_KEY,
  PROJECT_ID_SEARCH_PARAM_KEY,
} from '@contentlab/config/constants';
import {getUploadLimits} from '@contentlab/config/uploadLimits';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import useManageDraftProject from '@contentlab/hooks/mutations/use-manage-draft-project/useManageDraftProject';
import useGenerateProject from '@contentlab/hooks/use-generate-project/useGenerateProject';
import useMergedAssetErrors from '@contentlab/hooks/use-merged-asset-errors';
import useReadyAssets from '@contentlab/hooks/use-ready-assets';
import contentLabStore, {
  contentLabActions,
} from '@contentlab/stores/contentLabStore';
import {Asset} from '@contentlab/types/asset';
import {FeatureName} from '@contentlab/types/project';
import {
  CONTENT_LAB_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import translate from '@/utils/translate';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import useDubbingLanguagesQuery from '@/hooks/content-studio/use-dubbing-languages-query';
import useFeatureAssets from '@/hooks/content-studio/use-feture-assets/useFeatureAssets';
import {useDebouncedCallback} from '@/hooks/use-debounce';

function DubPage() {
  const {draftDubbingProject} = contentLabStore(
    useShallow((state) => ({
      draftDubbingProject: state.draftDubbingProject,
    }))
  );

  const {uploadedVideos, assetErrors: clientAssetErrors} = useFeatureAssets(
    FeatureName.AI_DUBBING
  );

  const uploadLimits = getUploadLimits(FeatureName.AI_DUBBING);

  const {
    assetErrors,
    invalidAssetIds,
    validUploadedVideos,
    validClientUploadedVideos,
  } = useMergedAssetErrors({
    serverAssetErrors:
      draftDubbingProject?.validationErrors?.userInput?.assets || [],
    clientAssetErrors,
    uploadedVideos,
  });

  const {data: dubbingLanguages = [], isLoading: isLoadingLanguages} =
    useDubbingLanguagesQuery();

  const languageGroups: LanguageGroup[] = useMemo(() => {
    // Transform string to LanguageOption
    const toLanguageOption = (name: string): LanguageOption => ({
      id: name,
      label: name,
      code: '', // DubPage doesn't use codes yet
    });

    const hardcodedPopularLanguageNames = [
      'English',
      'Spanish',
      'French',
      'German',
      'Portuguese',
      'Chinese (Mandarin, Simplified)',
    ].map((lang) => lang.toLowerCase());

    // Filter available languages to get popular ones
    const popularLanguages = dubbingLanguages
      .filter((lang) =>
        hardcodedPopularLanguageNames.includes(lang.toLowerCase())
      )
      .sort((a, b) => {
        const indexA = hardcodedPopularLanguageNames.indexOf(a.toLowerCase());
        const indexB = hardcodedPopularLanguageNames.indexOf(b.toLowerCase());
        return indexA - indexB;
      })
      .map(toLanguageOption);

    // Create All group - all languages sorted alphabetically
    const allLanguages = [...dubbingLanguages]
      .sort((a, b) => a.localeCompare(b))
      .map(toLanguageOption);

    return [
      new LanguageGroup(translate('Popular'), popularLanguages, 2),
      new LanguageGroup(translate('All'), allLanguages, 2),
    ];
  }, [dubbingLanguages]);
  const {uiInteraction} = useUiInteractionEvent();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    createDraftProject,
    updateDraftProject,
    resetDraftProject,
    isLoading: isManagingProjectLoading,
  } = useManageDraftProject({
    type: ProjectType.DUBBING,
    draftProject: draftDubbingProject,
    projectId: searchParams.get(PROJECT_ID_SEARCH_PARAM_KEY),
    onGetProjectErrorCallback: () => {
      searchParams.delete(PROJECT_ID_SEARCH_PARAM_KEY);
      setSearchParams(searchParams);
    },
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const readyAssets = useReadyAssets({
    assetIds: validClientUploadedVideos.map(
      (uploadedVideo) => uploadedVideo.id
    ),
    onReady: (assets) => updateOrCreateProject(assets),
    keepPreviousData: false,
  });

  const getProjectName = () =>
    contentLabStore.getState().draftDubbingProject.projectName;

  const getDubbingSpecs = useCallback(
    (assets?: Asset[], excludeInvalidAssets = false) => {
      const lipSync =
        contentLabStore.getState().draftDubbingProject.enableLipSync;
      const languages =
        contentLabStore.getState().draftDubbingProject.selectedLanguages;

      const assetsSource = assets || readyAssets;

      const assetsToUse = excludeInvalidAssets
        ? assetsSource.filter((asset: Asset) => !invalidAssetIds.has(asset.id))
        : assetsSource;

      return assetsToUse.flatMap((video) =>
        languages.map((language) => ({
          assetId: video.id,
          outputLanguage: language,
          enableLipSync: lipSync,
          numOfSpeakers: 1,
        }))
      );
    },
    [readyAssets, invalidAssetIds]
  );

  const createProject = async (assets?: Asset[]) => {
    if (draftDubbingProject.isInitialized) {
      return;
    }

    try {
      uiInteraction({
        item_name: 'create_dub_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
        flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
        interaction_id: uuid(),
      });

      await createDraftProject({
        type: ProjectType.DUBBING,
        projectName: getProjectName(),
        draft: {
          dubbingSpecs: getDubbingSpecs(assets),
        },
      });
    } catch (error) {
      // No operation
    }
  };
  const updateProject = async (
    status: DraftProjectStatus = DraftProjectStatus.UPDATE_DRAFT,
    assets?: Asset[]
  ) => {
    if (!draftDubbingProject.isInitialized) {
      return;
    }

    uiInteraction({
      item_name: 'update_dub_project',
      interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
      flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
      interaction_id: uuid(),
    });

    await updateDraftProject({
      status,
      projectName: getProjectName(),
      draft: {
        dubbingSpecs: getDubbingSpecs(
          assets,
          status === DraftProjectStatus.EXECUTE
        ),
      },
    });
  };

  const updateOrCreateProject = async (assets?: Asset[]) => {
    if (!draftDubbingProject.isInitialized) {
      await createProject(assets);
    } else {
      await updateProject(DraftProjectStatus.UPDATE_DRAFT, assets);
    }
  };

  const debouncedUpdateOrCreateProject = useDebouncedCallback(
    updateOrCreateProject,
    DEFAULT_DEBOUNCE_DELAY
  );

  const getRequiredCredits = () => {
    if (validUploadedVideos.length === 0) {
      return 0;
    }
    return (
      calculateVideoTokensForAssets(validUploadedVideos) *
      draftDubbingProject.selectedLanguages.length
    );
  };

  const generateProject = useGenerateProject({
    getRequiredCredits,
    setIsSubmitting,
    draftProject: draftDubbingProject,
    defaultProjectName: 'Dub',
    updateProject,
    resetProject: resetDraftProject,
  });

  const handleGenerateClick = async () => {
    setIsSubmitting(true);
    try {
      uiInteraction({
        item_name: 'execute_dub_project',
        interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
        flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
        interaction_id: uuid(),
      });

      await generateProject();
    } catch (error) {
      // No operation
    }
    setIsSubmitting(false);
  };

  const isGenerateButtonDisabled = useMemo(
    () =>
      uploadedVideos.length === 0 ||
      draftDubbingProject.selectedLanguages.length === 0 ||
      uploadedVideos.some((asset) => asset.isUploading) ||
      isSubmitting ||
      !draftDubbingProject.projectName ||
      getDubbingSpecs(readyAssets, true).length === 0 ||
      isManagingProjectLoading ||
      readyAssets.length !== validClientUploadedVideos.length,
    [
      uploadedVideos,
      validClientUploadedVideos,
      draftDubbingProject.selectedLanguages,
      isSubmitting,
      draftDubbingProject.projectName,
      isManagingProjectLoading,
      readyAssets,
      getDubbingSpecs,
    ]
  );

  const isGenerateButtonLoading = useMemo(
    () =>
      isSubmitting ||
      uploadedVideos.some((asset) => asset.isUploading) ||
      isManagingProjectLoading,
    [isSubmitting, uploadedVideos, isManagingProjectLoading]
  );

  const generateProjectDescription = useMemo(() => {
    if (isSubmitting) {
      return '';
    }
    const isUploading = uploadedVideos.some((video) => video.isUploading);
    if (!isUploading && uploadedVideos.length === 0) {
      return translate('Upload videos and choose languages to generate');
    }
    if (
      uploadedVideos.length > 0 &&
      !draftDubbingProject.selectedLanguages.length
    ) {
      return translate('Choose languages to generate');
    }
    return '';
  }, [uploadedVideos, isSubmitting, draftDubbingProject.selectedLanguages]);

  return (
    <FeatureEditor
      feature={FeatureName.AI_DUBBING}
      featureDetailsProps={{
        featureDetailsComponent: (
          <Box sx={{paddingTop: '24px'}}>
            <LanguageSelector
              languageGroups={languageGroups}
              selectedLanguages={draftDubbingProject.selectedLanguages}
              setSelectedLanguages={(languages) => {
                uiInteraction({
                  item_name: 'choose_languages',
                  interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
                  flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
                  interaction_details: JSON.stringify({
                    languages: languages.join(','),
                  }),
                });
                contentLabActions.updateDraftDubbingProjectLanguages(languages);
                debouncedUpdateOrCreateProject();
              }}
              isLoading={isLoadingLanguages}
              searchQueryCleared={() => {
                uiInteraction({
                  item_name: 'clear_search_query',
                  interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
                  flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
                });
              }}
              searchQueryChanged={(query) => {
                uiInteraction({
                  item_name: 'search_query_changed',
                  interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
                  flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
                  interaction_details: JSON.stringify({query}),
                });
              }}
              clearAllPressed={() => {
                uiInteraction({
                  item_name: 'clear_all',
                  interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
                  flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
                });
              }}
              additionalHeaderContent={
                <LipSyncToggle
                  title={translate(
                    'Sync translated audio with mouth movements. Turn off for voiceover‒only videos to save processing time.'
                  )}
                  onChange={(checked) => {
                    uiInteraction({
                      item_name: 'lip_sync_toggle',
                      interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
                      flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
                      interaction_details: checked ? 'on' : 'off',
                    });
                    contentLabActions.updateDraftDubbingProjectLipSync(checked);
                    debouncedUpdateOrCreateProject();
                  }}
                  enabled={draftDubbingProject.enableLipSync}
                />
              }
            />
          </Box>
        ),
      }}
      renameProjectProps={{
        projectName: draftDubbingProject.projectName,
        onBlur: (name) => {
          updateOrCreateProject();
          uiInteraction({
            item_name: 'rename_project',
            interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
            flow_name: FLOW_NAMES.CONTENT_LAB.DUB,
            interaction_details: JSON.stringify({
              project_name: name,
            }),
          });
        },
        projectNameChange: (name) => {
          contentLabActions.updateDraftDubbingProjectName(name);
        },
        projectNamePlaceholder: translate('Name your Dub project'),
      }}
      generateProjectProps={{
        description: generateProjectDescription,
        assetErrors,
        readyAssets,
        onGenerateClick: handleGenerateClick,
        creditsCost: getRequiredCredits(),
        disabled: isGenerateButtonDisabled,
        isLoading: isGenerateButtonLoading,
        tooltipText: creditsTooltipTextForFeature(
          FeatureName.AI_DUBBING,
          validUploadedVideos.length,
          draftDubbingProject.selectedLanguages.length
        ),
      }}
      fileUploaderProps={{
        title: translate('Upload videos with speech'),
        guideTitle: translate('must_contain_clear_speech_with_limits', {
          maxDuration: translate(uploadLimits.maxDurationDisplayKey),
          maxSize: translate(uploadLimits.maxFileSizeDisplayKey),
        }),
        guideDescription: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.AI_DUBBING]
        ),
        shortenGuideText: translate(
          FEATURE_BEST_PRACTICES_LOCALE_KEY[FeatureName.AI_DUBBING]
        ),
        readyAssets,
        assetErrors,
        onLearnMoreClick: () => {
          contentLabActions.setIsLearnMoreModalOpen(true);
          uiInteraction({
            item_name: 'learn more',
            interaction_location: CONTENT_LAB_VIEW_NAMES.DUB,
          });
        },
      }}
    />
  );
}

export default DubPage;
